import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

/** One AP's count of a client's traffic, from the AP's side: `bitsIn` is what the AP received
 *  from the client, `bitsOut` what it sent to it. `[epochMillis, bitsPerSecond]`; null is unknown. */
export interface ClientTrafficSeries {
  apDeviceId: string | null;
  /** Null when the caller may not read the AP. */
  apDeviceName: string | null;
  bitsIn: [number, number | null][];
  bitsOut: [number, number | null][];
}

export interface ClientTraffic {
  start: number;
  end: number;
  series: ClientTrafficSeries[];
}

/** A wireless client's traffic as its APs counted it — the AP's side of its link. */
export function useEndpointClientTraffic(endpointId: string | undefined, bounds: { start: number; end: number } | null) {
  const contextReady = useActiveContextReady();
  return useQuery<ClientTraffic>({
    queryKey: ['endpoint-client-traffic', endpointId, bounds],
    queryFn: async () => {
      if (!endpointId || !bounds) throw new Error('An endpoint and a window are required.');
      const response = await axios.get<ClientTraffic>(
        `${config.backendApi}/api/endpoints/${endpointId}/client-traffic`,
        { params: { start: bounds.start, end: bounds.end }, withCredentials: true },
      );
      return response.data;
    },
    enabled: !!endpointId && !!bounds && contextReady,
    staleTime: 30_000,
  });
}
